// src/components/nav/mobile-nav.tsx
'use client';

import { useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Image from 'next/image';
import { Menu, Calendar, User, Star, Mail, LogOut } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/auth-store';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { useTranslations } from 'next-intl';

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations();
  const doLogout = useAuthStore((state) => state.doLogout);

  const handleLogout = async () => {
    setOpen(false);
    doLogout();
    router.push('/');
  };

  const handleNavClick = (href: string) => {
    setOpen(false);
    router.push(href);
  };

  // Main nav items
  const navItems = [
    { label: t('myEvents'), href: '/events', icon: Calendar },
    { label: t('myAccount'), href: '/profile', icon: User },
    { label: t('eventBestPractices'), href: '/event-best-practices', icon: Star },
    { label: t('contact'), href: '/help', icon: Mail },
  ];

  return (
    <header className="sticky top-0 z-50 h-20 w-full border-b bg-white">
      <div className="flex h-full items-center justify-between px-4">
        {/* Logo */}
        <div className="flex flex-col items-start">
          <Image
            src="/images/Embrace_for_Good_Black_Logo.png"
            alt="Lincoln Embrace for Good"
            width={150}
            height={45}
            priority
          />
        </div>

        {/* Hamburger Menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10">
              <Menu className="h-6 w-6" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <div className="flex h-full flex-col">
              {/* Drawer Header */}
              <div className="flex items-center justify-between border-b p-4">
                <Image
                  src="/images/Embrace_for_Good_Black_Logo.png"
                  alt="Lincoln Embrace for Good"
                  width={150}
                  height={45}
                />
              </div>

              {/* Navigation Items */}
              <nav className="flex-1 py-4">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.href}
                      onClick={() => handleNavClick(item.href)}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors',
                        'hover:bg-gray-100',
                        pathname === item.href
                          ? 'bg-gray-100 font-medium text-gray-900'
                          : 'text-gray-600'
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </button>
                  );
                })}

                <Separator className="my-2" />

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-600 transition-colors hover:bg-gray-100"
                >
                  <LogOut className="h-5 w-5" />
                  {t('logout')}
                </button>
              </nav>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
